import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, Hand, Calendar, UserCheck } from 'lucide-react';

export default function ReferralManagementPage() {
  const { user, axiosAuth } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [peers, setPeers] = useState([]);
  const [assignPeer, setAssignPeer] = useState({});
  const api = axiosAuth();
  const canAssign = ['coordinator', 'admin'].includes(user.role);

  const load = () => {
    api.get('/public/referrals').then(r => setReferrals(r.data)).catch(() => {});
    if (canAssign) api.get('/users?role=peer_specialist').then(r => setPeers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleInterest = async (id) => {
    try { await api.put(`/public/referrals/${id}/interest`); toast.success('Interest marked!'); load(); } catch { toast.error('Failed'); }
  };

  const handleAssign = async (id) => {
    if (!assignPeer[id]) return toast.error('Select a peer specialist');
    try { await api.put(`/public/referrals/${id}/assign?peer_id=${assignPeer[id]}`); toast.success('Referral assigned!'); load(); } catch { toast.error('Failed'); }
  };

  const statusColor = { pending: 'bg-amber-50 text-amber-700', assigned: 'bg-emerald-50 text-emerald-700' };

  return (
    <div className="space-y-6" data-testid="referral-mgmt-page">
      <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Referral Waitlist</h1><p className="text-muted-foreground text-sm mt-1">Manage incoming referrals</p></div>

      <div className="space-y-3">
        {referrals.length > 0 ? referrals.map(r => (
          <Card key={r.id} className="border-0 shadow-sm rounded-2xl" data-testid={`referral-${r.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-teal-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{r.name}</p>
                    <Badge className={statusColor[r.status]}>{r.status}</Badge>
                    {r.age_range && <Badge variant="secondary" className="text-xs">{r.age_range}</Badge>}
                  </div>
                  {r.referral_source && <p className="text-xs text-muted-foreground mt-1">Source: {r.referral_source}</p>}
                  {r.description && <p className="text-sm text-muted-foreground mt-1">{r.description}</p>}
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Calendar size={10} />{new Date(r.created_at).toLocaleDateString()}</p>
                  {r.interested_peers?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">{r.interested_peers.map((p, i) => <Badge key={`item-${i}`} variant="secondary" className="text-xs gap-1"><Hand size={10} />{p.peer_name}</Badge>)}</div>
                  )}
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {user.role === 'peer_specialist' && r.status === 'pending' && (
                      <Button size="sm" variant="outline" className="rounded-full text-xs h-7 gap-1" onClick={() => handleInterest(r.id)} data-testid={`interest-${r.id}`}><Hand size={12} /> I'm Interested</Button>
                    )}
                    {canAssign && r.status === 'pending' && (
                      <div className="flex items-center gap-1">
                        <Select value={assignPeer[r.id] || ''} onValueChange={v => setAssignPeer({...assignPeer, [r.id]: v})}>
                          <SelectTrigger className="h-7 text-xs w-40"><SelectValue placeholder="Assign to..." /></SelectTrigger>
                          <SelectContent>{peers.map(p => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" className="h-7 rounded-full text-xs bg-teal-600 hover:bg-teal-700 text-white gap-1" onClick={() => handleAssign(r.id)} data-testid={`assign-${r.id}`}><UserCheck size={12} /> Assign</Button>
                      </div>
                    )}
                    {r.assigned_to && <Badge className="bg-emerald-50 text-emerald-700 text-xs gap-1"><UserCheck size={10} />Assigned</Badge>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No referrals in the waitlist.</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
